import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { 
  sendPhoneOtp, 
  verifyPhoneOtp, 
  formatPhoneToE164 
} from '@/utils/supabase/auth/phoneAuth';
import { checkPhoneExists } from '@/utils/supabase/courierQueries';
import { supabase } from '@/integrations/supabase/client';
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useIsMobile } from '@/hooks/use-mobile';
import { buildLogoUrl } from '@/utils/supabaseConfig';

type AuthMethod = 'phone' | 'email';

const LoginPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [authMethod, setAuthMethod] = useState<AuthMethod>('phone');
  
  // Данные для авторизации по телефону
  const [phone, setPhone] = useState('');
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [otpError, setOtpError] = useState('');
  
  // Данные для авторизации по email
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Общие данные
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [termsError, setTermsError] = useState('');
  
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      if (user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/courier');
      }
    }
  }, [user, navigate]);

  // Форматирование номера телефона для отображения
  const formatPhoneInput = (input: string): string => {
    let digits = input.replace(/\D/g, '');
    
    if (digits.startsWith('8') && digits.length > 0) {
      digits = '7' + digits.substring(1);
    }
    
    if (!digits.startsWith('7') && digits.length > 0) {
      digits = '7' + digits;
    }
    
    if (digits.length > 0) {
      digits = '+' + digits;
    }
    if (digits.length > 2) {
      digits = digits.slice(0, 2) + ' (' + digits.slice(2);
    }
    if (digits.length > 7) {
      digits = digits.slice(0, 7) + ') ' + digits.slice(7);
    }
    if (digits.length > 12) {
      digits = digits.slice(0, 12) + '-' + digits.slice(12);
    }
    if (digits.length > 15) {
      digits = digits.slice(0, 15) + '-' + digits.slice(15);
    }

    return digits;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedPhone = formatPhoneInput(e.target.value);
    setPhone(formattedPhone);
    setPhoneError('');
  };

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setPhoneError('');
    setTermsError('');
    
    // Проверяем согласие с условиями
    if (!agreedToTerms) {
      setTermsError('Необходимо согласиться с политикой конфиденциальности и обработкой персональных данных');
      setIsLoading(false);
      return;
    }
    
    const digits = phone.replace(/\D/g, '');
    if (!digits || digits.length < 11) {
      setPhoneError('Введите корректный номер телефона');
      setIsLoading(false);
      return;
    }
    
    try {
      const formattedPhone = '+' + digits;
      console.log('Проверяем номер:', formattedPhone);
      
      // Для теста всегда разрешаем номер +71234567890
      if (formattedPhone === '+71234567890') {
        console.log('Используем тестовый номер телефона');
        const { data } = await sendPhoneOtp(formattedPhone);
        toast.success('Тестовый код отправлен! Используйте 123456');
        setIsOtpSent(true);
        setIsLoading(false);
        return;
      }
      
      // СНАЧАЛА проверяем блокировку в таблице profiles
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('is_blocked, role')
        .eq('phone', formattedPhone)
        .maybeSingle();
      
      if (profileError) {
        console.error('Ошибка при проверке профиля:', profileError);
      }
      
      // Если найден профиль и пользователь заблокирован
      if (profileData?.is_blocked) {
        toast.error('Ваш аккаунт заблокирован. Обратитесь к администратору для разблокировки.');
        setPhoneError('Аккаунт заблокирован');
        setIsLoading(false);
        return;
      }
      
      // Если найден администратор - разрешаем вход
      if (profileData?.role === 'admin') {
        console.log('Найден профиль администратора для номера:', formattedPhone);
        const { data } = await sendPhoneOtp(formattedPhone);
        toast.success('Код отправлен на ваш телефон');
        setIsOtpSent(true);
        setIsLoading(false);
        return;
      }

      // Если это не администратор и не заблокирован, проверяем наличие в таблице курьеров
      const { exists, error } = await checkPhoneExists(formattedPhone);
      
      console.log('Результат проверки курьеров:', exists, error);
      
      if (!exists) {
        toast.error(
          <div className="flex flex-col gap-2">
            <p>Ваш номер телефона не зарегистрирован в системе.</p>
            <p>Для начала работы оформите аренду в Telegram:</p>
            <a 
              href="https://t.me/electrocourier_bot" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-blue-500 hover:text-blue-600"
            >
              <span>@electrocourier_bot</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-telegram">
                <path d="M21.5 2h-19C1.7 2 1 2.7 1 3.5v17C1 21.3 1.7 22 2.5 22h19c.8 0 1.5-.7 1.5-1.5v-17C23 2.7 22.3 2 21.5 2zM12 19c-3.9 0-7-3.1-7-7s3.1-7 7-7 7 3.1 7 7-3.1 7-7 7z"/>
              </svg>
            </a>
          </div>,
          {
            duration: 10000,
          }
        );
        setPhoneError('Для доступа необходимо оформить аренду');
        throw new Error('Номер не зарегистрирован');
      }

      if (error) {
        setPhoneError(error.message || 'Произошла ошибка при проверке номера');
        throw new Error(error.message || 'Произошла ошибка при проверке номера');
      }

      console.log('Отправляем OTP на номер:', formattedPhone);
      const { data, error: otpError } = await sendPhoneOtp(formattedPhone);
      
      if (otpError) {
        setPhoneError(otpError.message || 'Произошла ошибка при отправке кода');
        throw new Error(otpError.message);
      }

      toast.success('Код отправлен на ваш телефон');
      setIsOtpSent(true);
    } catch (error: any) {
      console.error('OTP error:', error);
      if (!error.message.includes('не зарегистрирован') && !error.message.includes('заблокирован')) {
        toast.error(error.message || 'Произошла ошибка');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setOtpError('');
    
    if (!otp || otp.length !== 6) {
      setOtpError('Введите полный код подтверждения (6 цифр)');
      setIsLoading(false);
      return;
    }
    
    // Используем функцию форматирования из courierQueries.ts
    const formattedPhone = formatPhoneToE164(phone);
    
    try {
      console.log('Проверяем OTP для номера:', formattedPhone, 'код:', otp);
      
      // Сначала проверяем блокировку перед верификацией OTP
      const { data: profileData } = await supabase
        .from('profiles')
        .select('is_blocked')
        .eq('phone', formattedPhone)
        .maybeSingle();
        
      if (profileData?.is_blocked) {
        toast.error('Ваш аккаунт заблокирован. Обратитесь к администратору.');
        setOtpError('Аккаунт заблокирован');
        setIsLoading(false);
        return;
      }
      
      const { data, error } = await verifyPhoneOtp(formattedPhone, otp);
      
      if (error) {
        // Переводим распространенные сообщения об ошибках на русский язык
        let russianErrorMessage = '';
        const errorMessage = error.message.toLowerCase();
        
        if (errorMessage.includes('invalid one-time password') || errorMessage.includes('invalid otp')) {
          russianErrorMessage = 'Неверный код подтверждения';
        } else if (errorMessage.includes('expired')) {
          russianErrorMessage = 'Срок действия кода истек. Запросите новый код';
        } else if (errorMessage.includes('too many requests')) {
          russianErrorMessage = 'Слишком много попыток. Попробуйте позже';
        } else {
          russianErrorMessage = 'Ошибка при проверке кода: ' + error.message;
        }
        
        setOtpError(russianErrorMessage);
        throw new Error(russianErrorMessage);
      }

      // Повторная проверка блокировки после успешной авторизации
      if (data?.user) {
        const { data: blockData } = await supabase
          .from('profiles')
          .select('is_blocked')
          .eq('phone', formattedPhone)
          .maybeSingle();
          
        if (blockData?.is_blocked) {
          await supabase.auth.signOut();
          toast.error('Ваш аккаунт заблокирован. Обратитесь к администратору.');
          return;
        }
      }
      
      toast.success('Вход выполнен успешно!');
    } catch (error: any) {
      console.error('Verification error:', error);
      if (!error.message.includes('заблокирован')) {
        toast.error(error.message || 'Неизвестная ошибка');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToPhone = () => {
    setIsOtpSent(false);
    setOtp('');
    setOtpError('');
  };

  // Обработчики для авторизации по email
  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setEmailError('');
    setPasswordError('');
    setTermsError('');
    
    // Проверяем согласие с условиями
    if (!agreedToTerms) {
      setTermsError('Необходимо согласиться с политикой конфиденциальности и обработкой персональных данных');
      setIsLoading(false);
      return;
    }
    
    // Валидация email
    if (!email || !email.includes('@')) {
      setEmailError('Введите корректный email');
      setIsLoading(false);
      return;
    }
    
    // Валидация пароля
    if (!password) {
      setPasswordError('Введите пароль');
      setIsLoading(false);
      return;
    }
    
    try {
      // Если это регистрация, необходимо проверить подтверждение пароля
      if (isRegistering) {
        if (password !== confirmPassword) {
          setPasswordError('Пароли не совпадают');
          setIsLoading(false);
          return;
        }
        
        if (password.length < 6) {
          setPasswordError('Пароль должен содержать не менее 6 символов');
          setIsLoading(false);
          return;
        }
        
        // Выполняем регистрацию
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              name: email.split('@')[0], // Имя по умолчанию из email
              role: 'courier'
            }
          }
        });
        
        if (error) {
          // Обрабатываем распространенные ошибки
          if (error.message.includes('already registered')) {
            setEmailError('Пользователь с таким email уже существует');
          } else {
            setEmailError(error.message || 'Ошибка при регистрации');
          }
          throw error;
        }
        
        toast.success('Регистрация успешна! Проверьте почту для подтверждения аккаунта.');
        // Переключаемся на вход после успешной регистрации
        setIsRegistering(false);
      } else {
        // Выполняем вход
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password
        });
        
        if (error) {
          // Обрабатываем распространенные ошибки
          if (error.message.includes('Invalid login credentials')) {
            setEmailError('Неверный email или пароль');
          } else if (error.message.includes('Email not confirmed')) {
            setEmailError('Необходимо подтвердить email. Проверьте свою почту.');
          } else {
            setEmailError(error.message || 'Ошибка при входе');
          }
          throw error;
        }
        
        toast.success('Вход выполнен успешно!');
      }
    } catch (error: any) {
      console.error('Email auth error:', error);
      if (!emailError) {
        toast.error(error.message || 'Произошла ошибка');
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  const handlePasswordReset = async () => {
    if (!email || !email.includes('@')) {
      setEmailError('Введите корректный email для сброса пароля');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      
      if (error) throw error;
      
      toast.success('Инструкции по сбросу пароля отправлены на вашу почту');
    } catch (error: any) {
      console.error('Password reset error:', error);
      toast.error(error.message || 'Ошибка при отправке сброса пароля');
    } finally {
      setIsLoading(false);
    }
  };

  // Переключение между методами авторизации
  const handleAuthMethodChange = (method: AuthMethod) => {
    setAuthMethod(method);
    setIsOtpSent(false);
    setOtp('');
    setOtpError('');
    setEmailError('');
    setPasswordError('');
    setPhoneError('');
    setTermsError('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-900 dark:to-slate-800 p-4">
      <Card className="w-[95%] max-w-[380px] shadow-xl border-0 overflow-hidden">
        <div className="bg-gradient-to-r from-tuktuk-dark/80 to-tuktuk-teal/80 py-4 px-4 flex flex-col items-center justify-center">
          <img 
            src={buildLogoUrl()} 
            alt="ТУК-ТУК PRO Logo" 
            className="w-28 h-28 mb-2 filter brightness-0 invert" 
          />
          <h1 className="text-2xl font-russo text-white mt-1 tracking-wider">ТУК-ТУК PRO</h1>
          <p className="text-sm text-white/80 font-medium">Профессиональная Аренда</p>
        </div>
        
        <CardHeader className="space-y-1 pt-4">
          <CardTitle className="text-xl font-bold text-center">
            Вход в систему
          </CardTitle>
          <CardDescription className="text-center text-sm">
            Введите данные для входа в систему
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <Tabs 
            defaultValue="phone" 
            value={authMethod} 
            onValueChange={(value) => handleAuthMethodChange(value as AuthMethod)}
            className="mb-4"
          >
            <TabsList className="grid grid-cols-2 w-full mb-2">
              <TabsTrigger value="phone">По номеру</TabsTrigger>
              <TabsTrigger value="email">По email</TabsTrigger>
            </TabsList>
            
            <TabsContent value="phone">
              {!isOtpSent ? (
                <form onSubmit={handleSendOtp} className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Номер телефона</Label>
                    <Input 
                      id="phone" 
                      type="tel" 
                      placeholder="(999) 123-45-67" 
                      value={phone}
                      onChange={handlePhoneChange}
                      required
                      className={`h-12 text-lg ${phoneError ? 'border-red-500' : ''}`}
                    />
                    {phoneError && <p className="text-sm text-red-500 mt-1">{phoneError}</p>}
                    <p className="text-xs text-slate-500 mt-1">
                      Введите номер телефона без кода страны
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-start space-x-2 mt-3">
                      <Checkbox 
                        id="terms" 
                        checked={agreedToTerms}
                        onCheckedChange={(checked) => {
                          setAgreedToTerms(checked === true);
                          if (checked) setTermsError('');
                        }}
                        className={termsError ? 'border-red-500 data-[state=checked]:bg-tuktuk-teal' : 'data-[state=checked]:bg-tuktuk-teal'}
                      />
                      <div className="grid gap-1.5 leading-none">
                        <label
                          htmlFor="terms"
                          className="text-xs text-muted-foreground leading-relaxed cursor-pointer"
                        >
                          Вводя номер телефона, ты соглашаешься с{" "}
                          <a 
                            href="/privacy-policy.html" 
                            className="text-tuktuk-teal hover:underline font-medium" 
                            target="_blank" 
                            rel="noopener noreferrer"
                          >
                            Политикой Конфиденциальности
                          </a>{" "}
                          и даешь{" "}
                          <a 
                            href="/personal-data.html" 
                            className="text-tuktuk-teal hover:underline font-medium" 
                            target="_blank" 
                            rel="noopener noreferrer"
                          >
                            Согласие на обработку персональных данных
                          </a>
                        </label>
                      </div>
                    </div>
                    {termsError && <p className="text-xs text-red-500 mt-1">{termsError}</p>}
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-tuktuk-teal hover:bg-tuktuk-dark transition-colors font-medium text-base h-12" 
                    disabled={isLoading}
                  >
                    {isLoading ? 'Отправка...' : 'Получить код'}
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleVerifyOtp} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="otp">Код подтверждения</Label>
                    <div className="flex justify-center my-4">
                      <InputOTP maxLength={6} value={otp} onChange={setOtp}>
                        <InputOTPGroup>
                          <InputOTPSlot index={0} />
                          <InputOTPSlot index={1} />
                          <InputOTPSlot index={2} />
                          <InputOTPSlot index={3} />
                          <InputOTPSlot index={4} />
                          <InputOTPSlot index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                    </div>
                    {otpError && <p className="text-sm text-red-500 text-center mt-1">{otpError}</p>}
                    <p className="text-sm text-center text-slate-500 mt-1">
                      Введите 6-значный код, отправленный на номер {formatPhoneInput(phone)}
                    </p>
                  </div>
                  
                  <div className="flex flex-col space-y-2">
                    <Button 
                      type="submit" 
                      className="w-full bg-tuktuk-teal hover:bg-tuktuk-dark transition-colors font-medium text-base h-12" 
                      disabled={isLoading || otp.length !== 6}
                    >
                      {isLoading ? 'Проверка...' : 'Подтвердить код'}
                    </Button>
                    
                    <div className="flex flex-col xs:flex-row gap-2 w-full">
                      <Button 
                        type="button" 
                        variant="outline"
                        className="w-full text-sm border-slate-300 h-10" 
                        onClick={handleBackToPhone}
                        disabled={isLoading}
                      >
                        Изменить номер
                      </Button>
                      
                      <Button 
                        type="button" 
                        variant="outline"
                        className="w-full text-sm border-slate-300 h-10" 
                        onClick={handleSendOtp}
                        disabled={isLoading}
                      >
                        Отправить повторно
                      </Button>
                    </div>
                  </div>
                </form>
              )}
            </TabsContent>
            
            <TabsContent value="email">
              <form onSubmit={handleEmailLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input 
                    id="email" 
                    type="email" 
                    placeholder="example@example.com" 
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailError('');
                    }}
                    required
                    className={`h-12 ${emailError ? 'border-red-500' : ''}`}
                  />
                  {emailError && <p className="text-sm text-red-500 mt-1">{emailError}</p>}
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label htmlFor="password">Пароль</Label>
                    {!isRegistering && (
                      <button
                        type="button"
                        onClick={handlePasswordReset}
                        className="text-xs text-tuktuk-teal hover:underline"
                      >
                        Забыли пароль?
                      </button>
                    )}
                  </div>
                  <Input 
                    id="password" 
                    type="password" 
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setPasswordError('');
                    }}
                    required
                    className={`h-12 ${passwordError ? 'border-red-500' : ''}`}
                  />
                  {passwordError && <p className="text-sm text-red-500 mt-1">{passwordError}</p>}
                </div>
                
                {isRegistering && (
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Подтвердите пароль</Label>
                    <Input 
                      id="confirmPassword" 
                      type="password" 
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="h-12"
                    />
                  </div>
                )}
                
                <div className="space-y-2">
                  <div className="flex items-start space-x-2 mt-3">
                    <Checkbox 
                      id="termsEmail" 
                      checked={agreedToTerms}
                      onCheckedChange={(checked) => {
                        setAgreedToTerms(checked === true);
                        if (checked) setTermsError('');
                      }}
                      className={termsError ? 'border-red-500 data-[state=checked]:bg-tuktuk-teal' : 'data-[state=checked]:bg-tuktuk-teal'}
                    />
                    <div className="grid gap-1.5 leading-none">
                      <label
                        htmlFor="termsEmail"
                        className="text-xs text-muted-foreground leading-relaxed cursor-pointer"
                      >
                        Вводя данные, ты соглашаешься с{" "}
                        <a 
                          href="/privacy-policy.html" 
                          className="text-tuktuk-teal hover:underline font-medium" 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          Политикой Конфиденциальности
                        </a>{" "}
                        и даешь{" "}
                        <a 
                          href="/personal-data.html" 
                          className="text-tuktuk-teal hover:underline font-medium" 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          Согласие на обработку персональных данных
                        </a>
                      </label>
                    </div>
                  </div>
                  {termsError && <p className="text-xs text-red-500 mt-1">{termsError}</p>}
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full bg-tuktuk-teal hover:bg-tuktuk-dark transition-colors font-medium text-base h-12" 
                  disabled={isLoading}
                >
                  {isLoading ? 'Загрузка...' : isRegistering ? 'Зарегистрироваться' : 'Войти'}
                </Button>
                
                <div className="text-center mt-4">
                  <button
                    type="button"
                    onClick={() => setIsRegistering(!isRegistering)}
                    className="text-tuktuk-teal text-sm hover:underline"
                  >
                    {isRegistering ? 'Уже есть аккаунт? Войти' : 'Нет аккаунта? Зарегистрироваться'}
                  </button>
                </div>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
